<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import { type Ref, computed } from 'vue'

import { useInjectIsPublicDashboard } from '@/dashboard/composables/useIsPublicDashboard'
import { type IFrameContent } from '@/dashboard/types/widget.ts'

import DashboardContentContainer from './DashboardContentContainer.vue'
import type { ContentProps } from './types.ts'

interface DashboardContentIFrameProps extends ContentProps<IFrameContent> {
  contentCenter?: boolean
  disableClickShield?: boolean
}

const {
  content,
  contentCenter,
  disableClickShield = false
} = defineProps<DashboardContentIFrameProps>()
const isPublicDashboard = useInjectIsPublicDashboard()

const isValidUrl: Ref<boolean> = computed(() => {
  try {
    return ['https:', 'http:'].includes(new URL(content.url).protocol)
  } catch {
    return false
  }
})
</script>

<template>
  <DashboardContentContainer
    :effective-title="effectiveTitle"
    :general_settings="general_settings"
    :content-center="contentCenter"
    :is-scrollable-preview="isPreview && isValidUrl"
  >
    <div
      class="db-content-i-frame__div"
      :class="{
        'db-content-i-frame__preview': isPreview && isValidUrl
      }"
    >
      <iframe
        :key="content.url"
        class="db-content-i-frame__iframe"
        allowtransparency="true"
        :src="content.url"
      />
      <div
        v-if="isPublicDashboard && !disableClickShield"
        class="db-content-i-frame__click-shield"
      />
    </div>
  </DashboardContentContainer>
</template>

<style scoped>
.db-content-i-frame__div {
  position: relative;
  height: 100%;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
}

.db-content-i-frame__click-shield {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.db-content-i-frame__iframe {
  height: 100%;
  width: 100%;
  border: none;
}

/* Overflowing of this preview iframe is handled in DashboardContentContainer's scroll container */
.db-content-i-frame__preview,
.db-content-i-frame__preview .db-content-i-frame__iframe {
  width: 100vw;
  height: 100vh;
}
</style>
